/* eslint-disable max-lines-per-function */

import { ref, readonly } from "vue";
import { isWidthAndHeightValid } from "components/jiffy/vue-product-details-dtf/ArtworkMultiSize/utils";
import ErrorTypes from "components/jiffy/vue-product-details-dtf/error-types";

// eslint-disable-next-line prettier/prettier
const noop = () => { };

const createError = (message, type) => ({ message, type });

export default function useArtworkValidation({
  minWidth = 0,
  minHeight = 0,
  maxWidth = 100,
  maxHeight = 100,
  isPrintAsIs = false,
  metadata = null,
  originalDimensions = {},
  onValidation = noop
} = {}) {
  const errors = ref({});
  const currentItem = ref(null);
  const originalArtworkDimensions = ref(originalDimensions);

  const validationErrorType = id => errors.value[id]?.type;

  const handleValidationError = (id, type, message) => {
    if (validationErrorType(id) !== type) {
      onValidation("image_validation_error", type, {});
    }
    const error = createError(message, type);
    errors.value[id] = error;
    return true;
  };

  const widthConstraint = () =>
    metadata.aspectRatio > 1 ? metadata.maxSheetHeight : metadata.maxSheetWidth;

  const heightConstraint = () =>
    metadata.aspectRatio > 1 ? metadata.maxSheetWidth : metadata.maxSheetHeight;

  const fitsGangSheet = ({ width, height }) =>
    width !== "" &&
    height !== "" &&
    Math.min(width, height) <= metadata?.maxSheetWidth &&
    Math.max(width, height) <= metadata?.maxSheetHeight;

  const invalidWidthValidation = () =>
    currentItem.value.width === "" &&
    handleValidationError(
      currentItem.value.id,
      ErrorTypes.INVALID_WIDTH,
      "Please enter a valid width."
    );

  const invalidHeightValidation = () =>
    currentItem.value.height === "" &&
    handleValidationError(
      currentItem.value.id,
      ErrorTypes.INVALID_HEIGHT,
      `Please enter a valid ${isPrintAsIs ? "length" : "height"}.`
    );

  const negativeValuesValidation = () =>
    (currentItem.value.width < 0 || currentItem.value.height < 0) &&
    handleValidationError(
      currentItem.value.id,
      ErrorTypes.NEGATIVE_VALUES,
      "Negative values are not permitted."
    );

  const minPrintSizeValidation = () =>
    !(currentItem.value.width >= minWidth && currentItem.value.height >= minHeight) &&
    handleValidationError(
      currentItem.value.id,
      ErrorTypes.MIN_PRINT_SIZE,
      `The minimum print size is ${minWidth}" x ${minHeight}${isPrintAsIs ? "'" : '"'}`
    );

  const maxPrintSizeValidation = () =>
    !isPrintAsIs &&
    !fitsGangSheet(currentItem.value) &&
    !(
      Number(currentItem.value.width) <= maxWidth && Number(currentItem.value.height) <= maxHeight
    ) &&
    handleValidationError(
      currentItem.value.id,
      ErrorTypes.MAX_PRINT_SIZE,
      `The maximum print size is ${maxWidth}" x ${maxHeight}"`
    );

  const maxPrintSizePrintAsIsValidation = () =>
    isPrintAsIs &&
    currentItem.value.height > (metadata?.maxSheetHeight || maxHeight) &&
    handleValidationError(
      currentItem.value.id,
      ErrorTypes.MAX_PRINT_SIZE_PRINT_AS_IS,
      `The maximum print size is ${minWidth}" x ${maxHeight}'`
    );

  const willFitGangSheet = () => {
    if (!metadata) return false;
    const isWidthValid = fitsGangSheet(currentItem.value)
      ? isWidthAndHeightValid(currentItem.value)
      : currentItem.value.width !== "" && currentItem.value.width <= widthConstraint();
    const isHeightValid = fitsGangSheet(currentItem.value)
      ? isWidthAndHeightValid(currentItem.value)
      : currentItem.value.height !== "" && currentItem.value.height <= heightConstraint();

    if (isWidthValid && isHeightValid) return false;

    errors.value[currentItem.value.id] = errors.value[currentItem.value.id]
      ? { ...errors.value[currentItem.value.id], isFieldValid: isWidthValid && isHeightValid }
      : {};

    return true;
  };

  const resetErrors = () => {
    errors.value = {};
  };

  const validateDimensions = item => {
    resetErrors();
    currentItem.value = item;

    const hasError = [
      invalidWidthValidation,
      invalidHeightValidation,
      negativeValuesValidation,
      maxPrintSizePrintAsIsValidation,
      minPrintSizeValidation,
      maxPrintSizeValidation
    ].some(validation => validation());

    if (!hasError) {
      willFitGangSheet();
    }

    return errors.value[currentItem.value.id];
  };

  return {
    errors: readonly(errors),
    currentItem: readonly(currentItem),
    originalArtworkDimensions: readonly(originalArtworkDimensions),
    validateDimensions,
    resetErrors
  };
}
